// @flow

import React, { useEffect, useRef, useState } from 'react';
import type { Node } from 'react';
import { FormattedMessage } from 'react-intl';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import { useSelector, shallowEqual } from 'react-redux';
import { selectUnitSystem } from 'shared_services/redux/selectors/unitSystem';
import { GridFluid, GridFluidItem } from '@riseart/grid';
import { ArtItem } from 'shared_components/arts/item/Item';
import { factoryFromArtFlat } from 'shared_models/api/artFlat';
import { art as ENUM_ART } from 'Enum';
import { useLocale } from 'shared_services/redux/hooks/useLocale';
import { useListTracking } from 'shared_services/redux/hooks/useListTracking';
import { GridNoResults } from 'shared_components/arts/containers/grid/NoResults';
import { PromotionalCardMessage } from 'shared_components/messages/PromotionalCard';

const LAZYLOAD_OFFSET_PX = 200;
const GRID_LAYOUT_DEBOUNCE_TIMEOUT = 500;

/**
 * hasImageWithoutDimensions
 * @param {Array<Object>} images
 * @returns {boolean}
 */
function hasImageWithoutDimensions(image: ?Object): boolean {
  if (!image) {
    return false;
  }

  return !(image.width && image.height);
}

type Props = {
  loading: boolean,
  items: Array<Object>,
  trackedItems?: Array<Object>,
  imageType: string,
  toggleArtTabHandler?: Function,
  wrapperPaddings?: Object,
  cardDisplayStyle: string | number,
  openInNewTab?: boolean,
  additionalActionControls?: Function,
  showAddToCart?: boolean,
  showRent?: boolean,
  showHoverContent?: boolean,
  tracking?: Object,
  noItems?: Node,
  lazyloadAfter?: number,
  disableHeadingTags?: boolean,
  cardProps?: Object | null,
};

/**
 * ArtGridFluid
 *
 * @param props
 * @returns {Node}
 */

export function ArtGridFluid(props: Props): Node {
  const [storeTrackedItems, setStoreTrackedItems] = useState(null);
  const masonryRef = useRef(null);
  const locale = useLocale();
  const { unitSystem } = useSelector(selectUnitSystem, shallowEqual);
  const trackedItems = props.trackedItems || props.items.filter(({ isPromotion }) => !isPromotion);
  const { trackListView, handleItemClick } = useListTracking(
    trackedItems,
    // Overwrite enableInitial as this is triggred by this component in the useEffect bellow
    props.tracking ? { ...props.tracking, enableInitial: false } : null,
  );
  const trackedItemsString = JSON.stringify(trackedItems);

  useEffect(() => {
    if (!props.loading && props.tracking && storeTrackedItems !== trackedItemsString) {
      trackListView(props.tracking.name, null, trackedItems);
      setStoreTrackedItems(trackedItemsString);
    }
    // eslint-disable-next-line
  }, [props.loading, props.tracking, trackedItemsString, trackListView]);

  /**
   * handleImageLoad
   *
   * Debounced function that is called on each image load from ArtItem
   */
  const handleImageLoad = debounce(() => {
    masonryRef &&
      masonryRef.current &&
      masonryRef.current.masonry &&
      masonryRef.current.masonry.layout();
  }, GRID_LAYOUT_DEBOUNCE_TIMEOUT);

  const {
    loading,
    items,
    imageType,
    toggleArtTabHandler,
    wrapperPaddings,
    cardDisplayStyle,
    openInNewTab = true,
    additionalActionControls = null,
    showAddToCart = false,
    showRent = false,
    showHoverContent = false,
    noItems,
    lazyloadAfter,
    disableHeadingTags,
    cardProps,
  } = props;

  const loadedImageType =
    imageType === ENUM_ART.list.LAYOUT_FLUID
      ? ENUM_ART.image.type.TYPE_MAIN_MASTER
      : ENUM_ART.image.type.TYPE_MAIN_SQUARE;

  return (
    <GridFluid
      ref={masonryRef}
      wrapperPaddings={wrapperPaddings}
      windowWidth={typeof window !== 'undefined' && window.innerWidth}
    >
      {() =>
        loading || (items && items.length) ? (
          items.map((item, itemIndex) => (
            <GridFluidItem key={`${imageType}${item.id}`}>
              {item.isPromotion ? (
                <PromotionalCardMessage {...item} />
              ) : (
                <ArtItem
                  disableHeadingTags={disableHeadingTags}
                  {...(cardProps ? { cardProps } : null)}
                  locale={locale}
                  openInNewTab={openInNewTab}
                  displayStyle={cardDisplayStyle}
                  additionalActionControls={
                    additionalActionControls && typeof additionalActionControls === 'function'
                      ? additionalActionControls(item)
                      : null
                  }
                  showAddToCart={showAddToCart}
                  showRent={showRent}
                  showHoverContent={showHoverContent}
                  unitSystem={unitSystem}
                  data={factoryFromArtFlat(item, locale)}
                  target={`art-detail-${item.id}`}
                  imageType={imageType}
                  toggleArtTabHandler={toggleArtTabHandler}
                  onItemClick={handleItemClick(item)}
                  lazyload={
                    !lazyloadAfter || lazyloadAfter <= itemIndex
                      ? { config: { once: true, offset: LAZYLOAD_OFFSET_PX } }
                      : null
                  }
                  {...(hasImageWithoutDimensions(find(item.images, { type: loadedImageType }))
                    ? { onImageLoad: handleImageLoad }
                    : {})}
                />
              )}
            </GridFluidItem>
          ))
        ) : (
          <GridNoResults>
            {noItems || (
              <FormattedMessage id="components.art.gridNoArtworksDefault">
                {(noItemsText: string) => noItemsText}
              </FormattedMessage>
            )}
          </GridNoResults>
        )
      }
    </GridFluid>
  );
}
